<template>
  <div
    class="modal fade"
    id="modalBlsFees"
    ref="modalEl"
    tabindex="-1"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-dialog-centered modal-xl">
      <div class="modal-content border-0">
        <div class="modal-header border-0 pb-0">
          <div>
            <h5 class="modal-title fw-bold mb-5">
              Frais du bon {{ note?.note_stock_ref }}
            </h5>
            <div v-if="note" class="fs-13 text-body-tertiary">
              {{ note.note_stock_agency_name }} · {{ getDate(note.created_at) }}
            </div>
          </div>
          <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
        </div>

        <div class="modal-body">
          <div v-if="loading" class="text-center text-muted py-5">
            <span class="spinner-border spinner-border-sm me-5"></span>
            Chargement...
          </div>

          <div v-else-if="loadError" class="alert alert-danger mb-0">
            {{ loadError }}
          </div>

          <!-- Fees already added: read-only lines -->
          <template v-else-if="isDone">
            <div class="alert alert-success d-flex align-items-center fs-14">
              <i class="flaticon-check me-8"></i>
              Les frais de ce bon ont été ajoutés. Ils seront repris dans la
              prochaine facture de chaque client.
            </div>
            <div class="table-responsive">
              <table class="table align-middle mb-0">
                <thead>
                  <tr>
                    <th class="ps-0">Client</th>
                    <th>Désignation</th>
                    <th class="text-end">Qté</th>
                    <th class="text-end">Frais / colis</th>
                    <th class="text-end">Total</th>
                    <th>Facture</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="!lines.length">
                    <td colspan="6" class="text-center text-muted ps-0">
                      Aucun frais (tous les clients étaient à 0).
                    </td>
                  </tr>
                  <tr v-for="line in lines" :key="line.id">
                    <td class="ps-0 fw-semibold">{{ line.cstmr_name }}</td>
                    <td>{{ line.designation }}</td>
                    <td class="text-end">{{ line.qty }}</td>
                    <td class="text-end">{{ money(line.price) }}</td>
                    <td class="text-end fw-semibold">{{ money(line.total) }}</td>
                    <td>
                      <span :class="`badge rounded-pill fs-12 text-outline-${invoicedState(line.invoiced).variant}`">
                        {{ invoicedState(line.invoiced).label }}
                      </span>
                    </td>
                  </tr>
                </tbody>
                <tfoot v-if="lines.length">
                  <tr>
                    <td colspan="4" class="text-end fw-bold ps-0">Total</td>
                    <td class="text-end fw-bold">{{ money(linesTotal) }}</td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </template>

          <!-- Fees form -->
          <form v-else novalidate @submit.prevent="submit">
            <div class="bg-light rounded-2 p-15 mb-20">
              <div class="row g-2 align-items-end">
                <div class="col-md-5">
                  <label class="form-label fs-13 fw-medium mb-5">Désignation (tous)</label>
                  <input v-model.trim="bulk.designation" type="text" class="form-control" maxlength="150" />
                </div>
                <div class="col-md-4">
                  <label class="form-label fs-13 fw-medium mb-5">Frais / colis (tous)</label>
                  <div class="input-group">
                    <input
                      v-model="bulk.price"
                      type="number"
                      min="0"
                      step="0.01"
                      class="form-control"
                      placeholder="0.00"
                      @keydown.enter.prevent="applyToAll"
                    />
                    <span class="input-group-text">{{ $devis }}</span>
                  </div>
                </div>
                <div class="col-md-3">
                  <button type="button" class="btn btn-outline-primary w-100" @click="applyToAll">
                    Appliquer à tous
                  </button>
                </div>
              </div>
            </div>

            <div class="table-responsive">
              <table class="table align-middle mb-0">
                <thead>
                  <tr>
                    <th class="ps-0">Client</th>
                    <th class="text-center">Colis</th>
                    <th style="min-width: 260px">Désignation</th>
                    <th style="width: 180px">Frais / colis</th>
                    <th class="text-end" style="width: 130px">Total</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="!form.length">
                    <td colspan="5" class="text-center text-muted ps-0">Aucun colis dans ce bon.</td>
                  </tr>
                  <tr v-for="row in form" :key="row.customer_id">
                    <td class="ps-0 fw-semibold">
                      {{ row.customer_name }}
                      <span v-if="row.is_custom" class="badge rounded-pill text-bg-info fs-11 ms-5">Personnalisé</span>
                    </td>
                    <td class="text-center">
                      <span class="badge rounded-pill text-bg-light fs-13">{{ row.nb_parcels }}</span>
                    </td>
                    <td>
                      <input
                        v-model.trim="row.designation"
                        type="text"
                        maxlength="150"
                        class="form-control"
                        :class="{ 'is-invalid': submitted && !row.designation }"
                      />
                    </td>
                    <td>
                      <div class="input-group has-validation">
                        <input
                          v-model="row.price"
                          type="number"
                          min="0"
                          step="0.01"
                          class="form-control"
                          placeholder="0.00"
                          :class="{ 'is-invalid': submitted && !isValidPrice(row.price) }"
                        />
                        <span class="input-group-text">{{ $devis }}</span>
                      </div>
                    </td>
                    <td class="text-end fw-semibold" :class="{ 'text-body-tertiary': !rowTotal(row) }">
                      {{ money(rowTotal(row)) }}
                    </td>
                  </tr>
                </tbody>
                <tfoot v-if="form.length">
                  <tr>
                    <td colspan="4" class="text-end fw-bold ps-0">Total à facturer</td>
                    <td class="text-end fw-bold text-primary fs-16">{{ money(formTotal) }}</td>
                  </tr>
                </tfoot>
              </table>
            </div>

            <p class="fs-13 text-body-tertiary mt-15 mb-0">
              Total = nombre de colis × frais par colis. Les frais proposés sont ceux du client (personnalisés) sinon les frais généraux, modifiables. Un client laissé à 0 n'est pas facturé.
              Les frais ne peuvent être ajoutés qu'une seule fois par bon.
            </p>
          </form>
        </div>

        <div class="modal-footer border-0 pt-0">
          <button type="button" class="btn btn-light" data-bs-dismiss="modal">
            {{ isDone ? "Fermer" : "Annuler" }}
          </button>
          <button
            v-if="!loading && !loadError && !isDone && form.length"
            type="button"
            class="btn btn-primary"
            :disabled="saving"
            @click="submit"
          >
            <span v-if="saving" class="spinner-border spinner-border-sm me-5"></span>
            Ajouter les frais
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, reactive, ref } from "vue";
import { Modal } from "bootstrap";
import moment from "moment";
import Swal from "sweetalert2";
import api from "@/utils/axios";
import { currency } from "@/utils/currency";
import { useAppToast } from "@/composables/useAppToast";

const DEFAULT_DESIGNATION = "Emballage";

export default {
  name: "FeesModal",
  emits: ["saved"],

  setup(_, { emit, expose }) {
    const { showToast } = useAppToast();

    const modalEl = ref<HTMLElement | null>(null);
    const loading = ref(false);
    const loadError = ref("");
    const saving = ref(false);
    const submitted = ref(false);

    const note = ref<any>(null);
    const lines = ref<any[]>([]);
    const form = ref<any[]>([]);
    const bulk = reactive({ designation: DEFAULT_DESIGNATION, price: "" as string | number });

    const isDone = computed(() => Number(note.value?.note_stock_fees) !== 0);

    const isValidPrice = (v) => v !== "" && v !== null && !isNaN(Number(v)) && Number(v) >= 0;
    const rowTotal = (row) => (isValidPrice(row.price) ? row.nb_parcels * Number(row.price) : 0);
    const formTotal = computed(() => form.value.reduce((sum, row) => sum + rowTotal(row), 0));
    const linesTotal = computed(() => lines.value.reduce((sum, l) => sum + Number(l.total), 0));

    const money = (v) => `${Number(v || 0).toFixed(2)} ${currency()}`;
    const getDate = (v) => moment(String(v)).format("YYYY-MM-DD HH:mm");

    function invoicedState(invoiced) {
      if (Number(invoiced) === 1) return { label: "Facturé", variant: "success" };
      if (Number(invoiced) === 2) return { label: "Annulé", variant: "danger" };
      return { label: "En attente", variant: "warning" };
    }

    async function open(noteId: number) {
      loading.value = true;
      loadError.value = "";
      submitted.value = false;
      note.value = null;
      lines.value = [];
      form.value = [];
      bulk.designation = DEFAULT_DESIGNATION;
      bulk.price = "";
      if (modalEl.value) Modal.getOrCreateInstance(modalEl.value).show();

      try {
        const { data } = await api.get(`/api/auth/bls/${noteId}/fees`);
        note.value = data.note;
        lines.value = data.lines;
        // suggested fee: the customer's own packaging fee, else the general one (editable)
        form.value = data.customers.map((c) => ({
          ...c,
          designation: DEFAULT_DESIGNATION,
          price: c.default_price > 0 ? c.default_price : "",
        }));
      } catch (error: any) {
        loadError.value = error?.response?.data?.message || "Impossible de charger les frais de ce bon.";
      } finally {
        loading.value = false;
      }
    }

    function applyToAll() {
      form.value.forEach((row) => {
        if (bulk.designation) row.designation = bulk.designation;
        if (isValidPrice(bulk.price)) row.price = bulk.price;
      });
    }

    async function submit() {
      submitted.value = true;
      if (form.value.some((row) => !row.designation || !isValidPrice(row.price))) {
        showToast("Merci de remplir la désignation et le frais de chaque client.", "warning");
        return;
      }

      const billed = form.value.filter((row) => rowTotal(row) > 0).length;
      const confirm = await Swal.fire({
        icon: "question",
        title: "Ajouter les frais ?",
        html: billed
          ? `<b>${money(formTotal.value)}</b> seront ajoutés à la prochaine facture de <b>${billed}</b> client(s).<br/>Cette action ne peut être faite qu'une fois.`
          : "Tous les frais sont à 0 : aucun client ne sera facturé et le bon sera marqué comme traité.",
        showCancelButton: true,
        confirmButtonText: "Oui, ajouter",
        cancelButtonText: "Annuler",
        confirmButtonColor: "#3085d6",
      });
      if (!confirm.isConfirmed) return;

      saving.value = true;
      try {
        await api.post(`/api/auth/bls/${note.value.id}/fees`, {
          lines: form.value.map((row) => ({
            customer_id: row.customer_id,
            designation: row.designation,
            price: Number(row.price),
          })),
        });
        showToast("Les frais ont bien été ajoutés.", "success");
        if (modalEl.value) Modal.getOrCreateInstance(modalEl.value).hide();
        emit("saved");
      } catch (error: any) {
        const res = error?.response;
        const firstError = res?.data?.errors && Object.values(res.data.errors)[0];
        showToast((firstError && firstError[0]) || res?.data?.message || "Erreur, merci de réessayer.", "error");
        // already added by someone else: show the current lines
        if (res?.status === 409) {
          emit("saved");
          open(note.value.id);
        }
      } finally {
        saving.value = false;
      }
    }

    expose({ open });

    return {
      modalEl,
      loading,
      loadError,
      saving,
      submitted,
      note,
      lines,
      form,
      bulk,
      isDone,
      isValidPrice,
      rowTotal,
      formTotal,
      linesTotal,
      money,
      getDate,
      invoicedState,
      applyToAll,
      submit,
    };
  },
};
</script>
